import * as N$3Dmol from "3dmol";
import React, { useEffect, useRef, useState } from "react";

type ViewerStyle = "stick" | "ball-and-stick" | "sphere";

const ATOM_COLORS: Record<string, string> = {
  H: "#EFEFEF",
  C: "#888888",
  N: "#3050F8",
  O: "#FF0D0D",
  F: "#90E050",
  Cl: "#1FF01F",
  Br: "#A62929",
  I: "#940094",
  S: "#FFFF30",
  P: "#FF8000",
  B: "#F0C8A0",
  Na: "#AB5CF2",
  K: "#8F40D4",
  Ca: "#3DFF00",
  Mg: "#8AFF00",
};

interface MoleculeViewerProps {
  sdf?: string;
}

interface AtomLegendProps {
  atomTypes: string[];
  showHydrogens: boolean;
}

const AtomLegend: React.FC<AtomLegendProps> = ({
  atomTypes,
  showHydrogens,
}) => {
  const filteredAtomTypes = showHydrogens
    ? atomTypes
    : atomTypes.filter((atomType) => atomType !== "H");

  if (filteredAtomTypes.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap justify-center p-2 border-t gap-x-4 gap-y-2">
      {filteredAtomTypes.map((atomType) => {
        const atomColor = ATOM_COLORS[atomType] || "#888888";
        return (
          <div key={atomType} className="flex items-center">
            <div
              className="w-4 h-4 mr-2 border border-gray-300 rounded-full"
              style={{ backgroundColor: atomColor }}
            />
            <span className="text-sm text-gray-600">{atomType}</span>
          </div>
        );
      })}
    </div>
  );
};

const extractAtomTypesFromSDF = (sdfData: string): string[] => {
  const atomTypes = new Set<string>();
  const lines = sdfData.split("\n");
  const countsLine = lines[3];

  if (countsLine) {
    const atomsCount = parseInt(countsLine.substring(0, 3).trim());
    for (let i = 0; i < atomsCount; i++) {
      const atomLine = lines[4 + i];
      if (atomLine) {
        const atomType = atomLine.substring(31, 34).trim();
        atomTypes.add(atomType);
      }
    }
  }

  return Array.from(atomTypes).sort();
};

const MoleculeViewer: React.FC<MoleculeViewerProps> = ({ sdf }) => {
  const viewerRef = useRef<N$3Dmol.GLViewer | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [moleculeData, setMoleculeData] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [showHydrogens, setShowHydrogens] = useState<boolean>(true);
  const [viewerStyle, setViewerStyle] = useState<ViewerStyle>("stick");
  const [atomTypes, setAtomTypes] = useState<string[]>([]);
  const filePath = sdf ? `/matched_sdf/LNP_${sdf}.sdf` : null;

  useEffect(() => {
    if (containerRef.current) {
      viewerRef.current = N$3Dmol.createViewer(containerRef.current, {
        backgroundColor: "white",
        antialias: true,
      });

      const handleResize = () => viewerRef.current?.resize();
      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
        if (viewerRef.current) {
          viewerRef.current.clear();
        }
      };
    }
  }, []);

  useEffect(() => {
    if (!filePath) {
      setMoleculeData(null);
      setAtomTypes([]);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    const abortController = new AbortController();

    fetch(filePath, { signal: abortController.signal })
      .then((response) => {
        if (!response.ok)
          throw new Error(`HTTP error! Status: ${response.status}`);
        return response.text();
      })
      .then((data) => {
        if (data.trim() === "") throw new Error("PDB data is empty.");
        setMoleculeData(data);
        const types = extractAtomTypesFromSDF(data);
        setAtomTypes(types);
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.error(`Error loading PDB file from ${filePath}:`, err);
          setError(`Failed to load PDB data. Please check if the file exists.`);
          setAtomTypes([]);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });

    return () => {
      abortController.abort();
    };
  }, [filePath]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !moleculeData) {
      viewer?.clear();
      viewer?.render();
      return;
    }

    try {
      viewer.clear();
      viewer.addModel(moleculeData, "sdf");

      switch (viewerStyle) {
        case "stick":
          viewer.setStyle({}, { stick: { radius: 0.15 } });
          break;
        case "ball-and-stick":
          viewer.setStyle(
            {},
            {
              stick: { radius: 0.1 },
              sphere: { scale: 0.25 },
            }
          );
          break;
        case "sphere":
          viewer.setStyle({}, { sphere: {} });
          break;
      }

      if (!showHydrogens) {
        viewer.setStyle({ elem: "H" }, {
          hidden: true,
        } as N$3Dmol.AtomStyleSpec);
      }

      viewer.zoomTo();
      viewer.render();
      setError(null);
    } catch (e) {
      console.error("Error rendering molecule:", e);
      setError("Failed to render the molecule from PDB data.");
    }
  }, [moleculeData, showHydrogens, viewerStyle]);

  return (
    <div className="flex flex-col w-full h-full bg-white shadow-md">
      <div className="flex flex-wrap items-center justify-between gap-4 p-2 border-b">
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            checked={showHydrogens}
            onChange={() => setShowHydrogens(!showHydrogens)}
            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-gray-700">Show Hydrogens</span>
        </label>

        <div className="flex items-center gap-2">
          <select
            value={viewerStyle}
            onChange={(e) => setViewerStyle(e.target.value as ViewerStyle)}
            className="p-2 text-sm bg-gray-100 border-gray-300 rounded-lg shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            aria-label="Molecule display style"
          >
            <option value="stick">Stick</option>
            <option value="ball-and-stick">Ball and Stick</option>
            <option value="sphere">Sphere</option>
          </select>
        </div>
      </div>

      <div className="relative flex-grow" style={{ minHeight: "300px" }}>
        <div ref={containerRef} className="absolute inset-0" />

        {isLoading && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center text-gray-600 bg-white bg-opacity-80">
            <svg
              className="w-8 h-8 text-blue-500 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span className="mt-2">Loading molecule...</span>
          </div>
        )}

        {error && !isLoading && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center p-4 text-center text-red-600 bg-white">
            <p className="font-semibold">Error</p>
            <p className="text-sm">{error}</p>
          </div>
        )}

        {!filePath && !isLoading && !error && (
          <div className="absolute inset-0 z-10 flex items-center justify-center text-gray-400">
            No molecule selected.
          </div>
        )}
      </div>
      {moleculeData && !isLoading && (
        <AtomLegend atomTypes={atomTypes} showHydrogens={showHydrogens} />
      )}
    </div>
  );
};

export default MoleculeViewer;
